import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toggleEnvironment } from '../../../utils/apiClient';
import { getAuthHeaders } from '../../../utils/api';
import { useNavigate } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import * as Form from '@radix-ui/react-form';
import * as Separator from '@radix-ui/react-separator';
import * as Slider from '@radix-ui/react-slider';
import { Button } from '@radix-ui/themes';
import TopBar from '../../../components/ui/TopBar/TopBar';
// Using text/emoji icons instead of lucide-react for now

interface RubricState {
  low: string;
  medium: string;
  high: string;
}

interface RangeSliderProps {
  totalMarks: number;
  showMediumRange: boolean;
  onRangeChange: (lowRange: string, mediumRange: string, highRange: string) => void;
  lowRange: string;
  mediumRange: string;
  highRange: string;
}

const RangeSlider: React.FC<RangeSliderProps> = ({
  totalMarks,
  showMediumRange,
  onRangeChange,
  lowRange,
  mediumRange,
  highRange
}) => {
  const [sliderValues, setSliderValues] = useState<number[]>([]);

  // Initialize slider values from props
  useEffect(() => {
    if (totalMarks > 0) {
      if (showMediumRange) {
        // For 3-range: [lowEnd, mediumEnd] - use provided ranges or calculate defaults
        let lowEnd, mediumEnd;
        
        if (lowRange && mediumRange) {
          // Use provided ranges
          lowEnd = parseInt(lowRange.split('-')[1]);
          mediumEnd = parseInt(mediumRange.split('-')[1]);
        } else {
          // Calculate better defaults for 3-range
          if (totalMarks === 3) {
            lowEnd = 0;
            mediumEnd = 1;
          } else {
            lowEnd = Math.max(0, Math.floor(totalMarks * 0.25));
            mediumEnd = Math.max(lowEnd + 1, Math.floor(totalMarks * 0.65));
          }
        }
        
        setSliderValues([lowEnd, mediumEnd]);
      } else {
        // For 2-range: [lowEnd]
        const lowEnd = lowRange ? parseInt(lowRange.split('-')[1]) : Math.floor(totalMarks * 0.3);
        setSliderValues([lowEnd]);
      }
    }
  }, [totalMarks, showMediumRange, lowRange, mediumRange, highRange]);

  // Update parent when slider values change
  const handleSliderChange = (values: number[]) => {
    setSliderValues(values);
    
    if (showMediumRange && values.length === 2) {
      const [lowEnd, mediumEnd] = values;
      const newLowRange = `0-${lowEnd}`;
      const newMediumRange = `${lowEnd + 1}-${mediumEnd}`;
      const newHighRange = `${mediumEnd + 1}-${totalMarks}`;
      onRangeChange(newLowRange, newMediumRange, newHighRange);
    } else if (!showMediumRange && values.length === 1) {
      const [lowEnd] = values;
      const newLowRange = `0-${lowEnd}`;
      const newHighRange = `${lowEnd + 1}-${totalMarks}`;
      onRangeChange(newLowRange, '', newHighRange);
    }
  };

  // Handle direct number input
  const handleDirectInput = (index: number, value: string) => {
    const numValue = parseInt(value);
    if (isNaN(numValue)) return;

    const newValues = [...sliderValues];
    
    if (showMediumRange && newValues.length === 2) {
      if (index === 0) {
        // Low end: constrain between 0 and mediumEnd-1
        newValues[0] = Math.max(0, Math.min(newValues[1] - 1, numValue));
      } else {
        // Medium end: constrain between lowEnd+1 and totalMarks-1
        newValues[1] = Math.max(newValues[0] + 1, Math.min(totalMarks - 1, numValue));
      }
    } else if (!showMediumRange && newValues.length === 1) {
      // Low end: constrain between 0 and totalMarks-1
      newValues[0] = Math.max(0, Math.min(totalMarks - 1, numValue));
    }
    
    handleSliderChange(newValues);
  };

  if (totalMarks <= 0) {
    return <div className="text-gray-500">Please enter total marks first</div>;
  }

  const getRangeInfo = () => {
    if (showMediumRange && sliderValues.length === 2) {
      const [lowEnd, mediumEnd] = sliderValues;
      return {
        low: { start: 0, end: lowEnd, label: `Low (0-${lowEnd})` },
        medium: { start: lowEnd + 1, end: mediumEnd, label: `Medium (${lowEnd + 1}-${mediumEnd})` },
        high: { start: mediumEnd + 1, end: totalMarks, label: `High (${mediumEnd + 1}-${totalMarks})` }
      };
    } else if (!showMediumRange && sliderValues.length === 1) {
      const [lowEnd] = sliderValues;
      return {
        low: { start: 0, end: lowEnd, label: `Low (0-${lowEnd})` },
        high: { start: lowEnd + 1, end: totalMarks, label: `High (${lowEnd + 1}-${totalMarks})` }
      };
    }
    return null;
  };

  const rangeInfo = getRangeInfo();
  if (!rangeInfo) return null;

  return (
    <div className="space-y-6">
      <div className="text-sm font-semibold text-black">Mark Ranges</div>
      
      {/* Visual representation */}
      <div className="relative">
        <div className="h-8 rounded-lg overflow-hidden border-2 border-gray-300 flex">
                    {/* Low Range */}
          <div 
            className="bg-red-400 flex items-center justify-center text-white text-xs font-bold"
            style={{ width: `${((rangeInfo.low.end - rangeInfo.low.start + 1) / (totalMarks + 1)) * 100}%` }}
          >
            {rangeInfo.low.label}
          </div>
          
          {/* Medium Range - only if 3-range mode */}
          {showMediumRange && rangeInfo && 'medium' in rangeInfo && rangeInfo.medium && (
            <div 
              className="bg-yellow-400 flex items-center justify-center text-black text-xs font-bold"
              style={{ width: `${((rangeInfo.medium.end - rangeInfo.medium.start + 1) / (totalMarks + 1)) * 100}%` }}
            >
              {rangeInfo.medium.label}
            </div>
          )}
          
          {/* High Range */}
          <div 
            className="bg-green-400 flex items-center justify-center text-white text-xs font-bold"
            style={{ width: `${((rangeInfo.high.end - rangeInfo.high.start + 1) / (totalMarks + 1)) * 100}%` }}
          >
            {rangeInfo.high.label}
          </div>
        </div>
      </div>

      {/* Radix UI Slider */}
      <div className="px-2">
        <Slider.Root
          value={sliderValues}
          onValueChange={handleSliderChange}
          max={totalMarks}
          min={0}
          step={1}
          className="relative flex items-center select-none touch-none w-full h-5"
        >
          <Slider.Track className="bg-gray-200 relative grow rounded-full h-2">
            <Slider.Range className="absolute bg-gray-400 rounded-full h-full" />
          </Slider.Track>
          
          {sliderValues.map((_, index) => (
            <Slider.Thumb
              key={index}
              className="block w-5 h-5 bg-white border-2 border-gray-400 rounded-full hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 cursor-grab active:cursor-grabbing"
            />
          ))}
        </Slider.Root>
        
        {/* Scale markers */}
        <div className="flex justify-between mt-2 text-xs text-gray-500">
          {Array.from({ length: Math.min(totalMarks + 1, 11) }, (_, i) => {
            const value = Math.round((i / (Math.min(totalMarks + 1, 11) - 1)) * totalMarks);
            return <span key={i}>{value}</span>;
          })}
        </div>
      </div>
      

    </div>
  );
};

const InsertNewQuestion: React.FC = () => {
  const [question, setQuestion] = useState<string>("");
  const [marks, setMarks] = useState<string>("");
  const [highMarkRange, setHighMarkRange] = useState<string>("");
  const [mediumMarkRange, setMediumMarkRange] = useState<string>("");
  const [lowMarkRange, setLowMarkRange] = useState<string>("");
  const [rubric, setRubric] = useState<RubricState>({ low: "", medium: "", high: "" });
  const [useAI, setUseAI] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [feedbackType, setFeedbackType] = useState<string>("");
  const [customFeedback, setCustomFeedback] = useState<string>("");
  const [rangeError, setRangeError] = useState<string>("");
  const [rubricGenerated, setRubricGenerated] = useState<boolean>(false);
  const [rubricMode, setRubricMode] = useState<'ai' | 'manual' | null>(null);
  const [sourceContext, setSourceContext] = useState<string>('');
  const [showMediumRange, setShowMediumRange] = useState<boolean>(false);
  const [showRubricEditor, setShowRubricEditor] = useState<boolean>(false);
  const navigate = useNavigate();
  const posthog = usePostHog();

  const questionTextareaRef = useRef<HTMLTextAreaElement>(null);
  const lowRubricRef = useRef<HTMLTextAreaElement>(null);
  const highRubricRef = useRef<HTMLTextAreaElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  const adjustTextareaHeight = (ref: React.RefObject<HTMLTextAreaElement>) => {
    const textarea = ref.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  };

  useEffect(() => {
    adjustTextareaHeight(questionTextareaRef);
  }, [question]);

  useEffect(() => {
    if (lowRubricRef.current) {
      adjustTextareaHeight(lowRubricRef);
    }
  }, [rubric.low]);

  useEffect(() => {
    if (highRubricRef.current) {
      adjustTextareaHeight(highRubricRef);
    }
  }, [rubric.high]);

  // Recalculate ranges when medium range is toggled
  useEffect(() => {
    if (marks) {
      calculateMarkRanges(marks);
    }
  }, [showMediumRange]);

  // Scroll to error message when it appears
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  const calculateMarkRanges = (totalMarks: string) => {
    const marks = parseInt(totalMarks);
    if (!marks || marks < 1) return;

    if (showMediumRange) {
      // 3-range system - better distribution
      if (marks === 1) {
        setLowMarkRange("0-0");
        setMediumMarkRange("");
        setHighMarkRange("1-1");
      } else if (marks === 2) {
        setLowMarkRange("0-0");
        setMediumMarkRange("1-1");
        setHighMarkRange("2-2");
      } else if (marks === 3) {
        setLowMarkRange("0-0");
        setMediumMarkRange("1-1");
        setHighMarkRange("2-3");
      } else {
        // For marks > 3, use proportional distribution
        const lowEnd = Math.max(0, Math.floor(marks * 0.25));
        const mediumEnd = Math.max(lowEnd + 1, Math.floor(marks * 0.65));
        setLowMarkRange(`0-${lowEnd}`);
        setMediumMarkRange(`${lowEnd + 1}-${mediumEnd}`);
        setHighMarkRange(`${mediumEnd + 1}-${marks}`);
      }
    } else {
      // 2-range system
      if (marks === 1) {
        setLowMarkRange("0-0");
        setHighMarkRange("1-1");
      } else if (marks === 2) {
        setLowMarkRange("0-1");
        setHighMarkRange("2-2");
      } else {
        const lowEnd = Math.floor(marks * 0.3);
        const midPoint = Math.ceil(marks * 0.6);
        setLowMarkRange(`0-${lowEnd}`);
        setHighMarkRange(`${midPoint}-${marks}`);
      }
    }
    setRangeError("");
  };

  const handleGenerateRubric = async () => {
    // Check for empty fields first
    if (!question.trim()) {
      setError("Please enter a question before generating a rubric.");
      return;
    }
    
    if (!marks || !highMarkRange || !lowMarkRange) {
      setError("Please provide all required details before generating a rubric.");
      return;
    }

    // Validate ranges
    if (!validateRangeBounds(lowMarkRange, true)) {
      setError(`Invalid low mark range. Must be between 0 and ${parseInt(marks)-1} (for ${marks} marks), and start number must be less than end number.`);
      return;
    }

    if (!validateRangeBounds(highMarkRange, false)) {
      setError(`Invalid high mark range. Must be between 1 and ${marks} (for ${marks} marks), and start number must be less than end number.`);
      return;
    }

    // Check if ranges overlap
    const lowParts = lowMarkRange.split('-').map(num => parseInt(num));
    const highParts = highMarkRange.split('-').map(num => parseInt(num));
    if (lowParts[1] >= highParts[0]) {
      setError("Low mark range and high mark range cannot overlap.");
      return;
    }

    // If all validations pass, proceed with generation
    setLoading(true);
    setError("");

    try {
      // Simple frontend - just send question data to backend
      const response = await axios.post(
        '/api/v1/stem@home/question/questions/generate-rubric',
        {
          question: question,
          marks: marks,
          lowMarkRange: lowMarkRange,
          highMarkRange: highMarkRange,
          mediumMarkRange: showMediumRange ? mediumMarkRange : undefined,
          sourceContext: sourceContext
        },
        {
          baseURL: toggleEnvironment(),
          headers: getAuthHeaders()
        }
      );

      // Backend handles all business logic and returns structured data
      if (response.data.status === 'success') {
        const rubricData = response.data.data.rubric;
        setRubric({
          low: rubricData.low || "",
          medium: rubricData.medium || "",
          high: rubricData.high || ""
        });
      } else {
        throw new Error(response.data.message || "Failed to generate rubric");
      }
      setRubricGenerated(true);
    } catch (err: any) {
      // Handle different types of errors appropriately
      if (err.response?.status === 401) {
        setError("Authentication required. Please log in again.");
      } else if (err.response?.status === 400) {
        setError(err.response.data?.message || "Invalid input. Please check your question details.");
      } else {
        setError("Failed to generate rubric. Please try again.");
      }
      console.error("Error generating rubric:", err);
      setRubricGenerated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if all required fields are filled
    if (!question.trim()) {
      setError("Please enter a question before saving.");
      return;
    }
    
    if (!marks || !highMarkRange || !lowMarkRange) {
      setError("Please provide all required details before saving.");
      return;
    }
    
    if (showMediumRange && !mediumMarkRange) {
      setError("Please provide medium mark range when medium range is enabled.");
      return;
    }

    // Validate ranges
    if (!validateRangeBounds(lowMarkRange, true)) {
      setError(`Invalid low mark range. Must be between 0 and ${parseInt(marks)-1} (for ${marks} marks), and start number must be less than end number.`);
      return;
    }

    if (!validateRangeBounds(highMarkRange, false)) {
      setError(`Invalid high mark range. Must be between 1 and ${marks} (for ${marks} marks), and start number must be less than end number.`);
      return;
    }

    // Validate medium range if it exists
    if (showMediumRange && mediumMarkRange) {
      if (!validateRangeBounds(mediumMarkRange, false)) {
        setError(`Invalid medium mark range. Must be between 1 and ${parseInt(marks)-1} (for ${marks} marks), and start number must be less than end number.`);
        return;
      }
    }

    // Check if ranges overlap
    const lowParts = lowMarkRange.split('-').map(num => parseInt(num));
    const highParts = highMarkRange.split('-').map(num => parseInt(num));
    
    if (showMediumRange && mediumMarkRange) {
      const mediumParts = mediumMarkRange.split('-').map(num => parseInt(num));
      if (lowParts[1] >= mediumParts[0] || mediumParts[1] >= highParts[0]) {
        setError("Mark ranges cannot overlap.");
        return;
      }
    } else {
      if (lowParts[1] >= highParts[0]) {
        setError("Low mark range and high mark range cannot overlap.");
        return;
      }
    }

    // Check if rubric is complete based on mode
    if (rubricMode === 'manual') {
      const requiredRubrics = ['low', 'high'];
      if (showMediumRange) {
        requiredRubrics.push('medium');
      }
      
      const missingRubrics = requiredRubrics.filter(type => !rubric[type as keyof RubricState]?.trim());
      if (missingRubrics.length > 0) {
        const rangeNames = missingRubrics.map(type => 
          type === 'low' ? 'low mark range' : 
          type === 'medium' ? 'medium mark range' : 
          'high mark range'
        );
        setError(`Please provide ${rangeNames.join(', ')} rubric${missingRubrics.length > 1 ? 's' : ''} before saving.`);
        return;
      }
    } else if (!rubricGenerated) {
      setError("Please generate a rubric before saving the question.");
      return;
    }

    try {
      const teacherId = localStorage.getItem('userId');
      console.log('TeacherId from localStorage:', teacherId);

      // Format the rubric as a single string with clear sections, including medium if it exists
      let formattedRubric = `Low Mark Range (${lowMarkRange}):\n${rubric.low}`;
      
      if (showMediumRange && mediumMarkRange && rubric.medium?.trim()) {
        formattedRubric += `\n\nMedium Mark Range (${mediumMarkRange}):\n${rubric.medium}`;
      }
      
      formattedRubric += `\n\nHigh Mark Range (${highMarkRange}):\n${rubric.high}`;

      const response = await axios.post(
        '/api/v1/stem@home/question/questions/create',
        {
          teacherId: teacherId,
          question: question,
          sourceContext: sourceContext,
          marks: parseInt(marks),
          highMarkRange: highMarkRange,
          mediumMarkRange: showMediumRange ? mediumMarkRange : undefined,
          lowMarkRange: lowMarkRange,
          rubric: formattedRubric
        },
        {
          baseURL: toggleEnvironment(),
          headers: getAuthHeaders()
        }
      );

      console.log('Response:', response);

      if (response.status === 201) {
        alert("Question saved successfully!");
        resetForm();
        // Redirect to the questions list page
        navigate('/view-questions');
      }
    } catch (err: any) {
      console.error("Full error object:", err);
      console.error("Error response data:", err.response?.data);
      console.error("Error status:", err.response?.status);
      setError(`Failed to save question: ${err.response?.data?.message || err.message}`);
    }
  };

  const addMediumRange = () => {
    setShowMediumRange(true);
    // useEffect will handle recalculation
  };

  const removeMediumRange = () => {
    setShowMediumRange(false);
    setMediumMarkRange("");
    // useEffect will handle recalculation
  };

  const handleRubricChoice = (method: 'manual' | 'ai') => {
    // Validate that question, total marks and ranges are set before allowing rubric creation
    if (!question.trim()) {
      setError("Please enter a question first before creating a rubric.");
      return;
    }
    
    if (!marks || !lowMarkRange || !highMarkRange) {
      setError("Please enter total marks first to set up mark ranges before creating a rubric.");
      return;
    }
    
    setRubricMode(method);
    setShowRubricEditor(true);
    setRubricGenerated(true);
    setError(""); // Clear any previous errors
  };

  const resetForm = () => {
    setQuestion("");
    setSourceContext("");
    setMarks("");
    setHighMarkRange("");
    setMediumMarkRange("");
    setLowMarkRange("");
    setRubric({ low: "", medium: "", high: "" });
    setFeedbackType("");
    setCustomFeedback("");
    setRubricGenerated(false);
    setRubricMode(null);
    setShowRubricEditor(false);
    setShowMediumRange(false);
  };

  const validateNumberInput = (value: string): boolean => {
    return /^\d*$/.test(value);
  };

  const validateRangeInput = (value: string): boolean => {
    return /^[\d-]*$/.test(value);
  };

  const validateRangeBounds = (range: string, isLow: boolean): boolean => {
    if (!marks) return false;
    const maxMarks = parseInt(marks);
    
    // Check if range has correct format (e.g., "0-2" or "4-6")
    const parts = range.split('-');
    if (parts.length !== 2) return false;
    
    const start = parseInt(parts[0]);
    const end = parseInt(parts[1]);
    
    // Check if numbers are valid
    if (isNaN(start) || isNaN(end)) return false;
    
    // Check bounds
    if (start < 0 || end > maxMarks || start >= end) return false;
    
    // Check bounds
    if (isLow && (start < 0 || end >= maxMarks)) return false;
    
    // For high range: must be between 1 and maxMarks
    if (!isLow && (start <= 0 || end > maxMarks)) return false;
    
    return true;
  };

  const handleMarksChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (validateNumberInput(value)) {
      setMarks(value);
      calculateMarkRanges(value);
      setRangeError("");
      
      // Automatically remove medium range if marks < 3
      if (parseInt(value) < 3 && showMediumRange) {
        setShowMediumRange(false);
        setMediumMarkRange("");
      }
    }
  };

  const handleLowRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (validateRangeInput(value)) {
      setLowMarkRange(value);
      // Only validate if we have a complete range format
      if (value.includes('-')) {
        if (!validateRangeBounds(value, true)) {
          setRangeError(`Low mark range must be between 0 and ${parseInt(marks)-1} (for ${marks} marks), and start number must be less than end number`);
        } else {
          setRangeError("");
        }
      }
    }
  };

  const handleHighRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (validateRangeInput(value)) {
      setHighMarkRange(value);
      // Only validate if we have a complete range format
      if (value.includes('-')) {
        if (!validateRangeBounds(value, false)) {
          setRangeError(`High mark range must be between 1 and ${marks} (for ${marks} marks), and start number must be less than end number`);
        } else {
          setRangeError("");
        }
      }
    }
  };

  const handleRangeChange = (newLowRange: string, newMediumRange: string, newHighRange: string) => {
    setLowMarkRange(newLowRange);
    if (showMediumRange) {
      setMediumMarkRange(newMediumRange);
    }
    setHighMarkRange(newHighRange);
    setRangeError("");
  };

  const handleSaveQuestion = async () => {
    try {
      // Check if question is provided
      if (!question.trim()) {
        setError("Please enter a question before saving.");
        return;
      }
      
      // Check if total marks are provided
      if (!marks) {
        setError("Please enter total marks before saving.");
        return;
      }
      
      // Check if rubrics are empty
      const requiredRubrics = ['low', 'high'];
      if (showMediumRange) {
        requiredRubrics.push('medium');
      }
      
      const missingRubrics = requiredRubrics.filter(type => !rubric[type as keyof RubricState]?.trim());
      if (missingRubrics.length > 0) {
        const rangeNames = missingRubrics.map(type => 
          type === 'low' ? 'low mark range' : 
          type === 'medium' ? 'medium mark range' : 
          'high mark range'
        );
        setError(`Please provide ${rangeNames.join(', ')} rubric${missingRubrics.length > 1 ? 's' : ''} before saving.`);
        return;
      }

      // Always show reminder before saving
      const proceed = window.confirm('Please make sure you have reviewed all mark range rubrics before saving. Do you want to proceed?');
      if (!proceed) {
        return;
      }

      // Get teacher ID from localStorage
      const userData = localStorage.getItem('user');
      const user = userData ? JSON.parse(userData) : null;
      const teacherId = user?.userId;

      // Format the rubric as a single string with clear sections, including medium if it exists
      let formattedRubric = `Low Mark Range (${lowMarkRange}):\n${rubric.low}`;
      
      if (showMediumRange && mediumMarkRange && rubric.medium?.trim()) {
        formattedRubric += `\n\nMedium Mark Range (${mediumMarkRange}):\n${rubric.medium}`;
      }
      
      formattedRubric += `\n\nHigh Mark Range (${highMarkRange}):\n${rubric.high}`;

      const response = await axios.post(
        '/api/v1/stem@home/question/questions/create',
        {
          teacherId: teacherId,
          question: question,
          sourceContext: sourceContext,
          marks: parseInt(marks),
          highMarkRange: highMarkRange,
          mediumMarkRange: showMediumRange ? mediumMarkRange : undefined,
          lowMarkRange: lowMarkRange,
          rubric: formattedRubric
        },
        {
          baseURL: toggleEnvironment(),
          headers: getAuthHeaders()
        }
      );

      console.log('Response:', response);

      if (response.status === 201) {
        alert("Question saved successfully!");
        
        // Track question creation in PostHog
        if (posthog) {
          posthog.capture('question_created', {
            question_length: question.length,
            total_marks: parseInt(marks),
            has_medium_range: showMediumRange,
            low_mark_range: lowMarkRange,
            medium_mark_range: showMediumRange ? mediumMarkRange : null,
            high_mark_range: highMarkRange,
            rubric_method: rubricMode,
            has_source_context: sourceContext.length > 0,
            source_context_length: sourceContext.length
          });
        }
        
        resetForm();
        // Redirect to the questions list page
        navigate('/view-questions');
      }
    } catch (err: any) {
      console.error("Full error object:", err);
      console.error("Error response data:", err.response?.data);
      console.error("Error status:", err.response?.status);
      setError(`Failed to save question: ${err.response?.data?.message || err.message}`);
    }
  };

  return (
    <>
      <TopBar 
        title="Insert New Question"
        showActions={true} 
      />
      <div className="p-4 md:p-8 w-full min-h-screen" style={{ paddingTop: '80px' }}>
        {/* Error Message */}
        {error && (
          <div ref={errorRef} className="border-2 border-red-300 bg-red-50 p-4 rounded-lg mb-6">
            <div className="flex items-start gap-2">
              <span className="text-red-600 text-lg">❌</span>
              <div className="text-red-800">
                <strong>Error:</strong> {error}
              </div>
            </div>
          </div>
        )}

        {/* Main Form */}
        <div className="space-y-6">
          <div className="shadow-xl border border-gray-200 rounded-lg bg-white">
            <div className="p-4 md:p-6 space-y-6">
              {/* Question Input */}
              <div className="space-y-2">
                <label className="text-base font-bold flex items-center gap-2 text-black">
                  Question{" "}
                  <span className="bg-red-600 text-white text-xs px-2 py-1 rounded">
                    Required
                  </span>
                  <span className={`text-xs ml-auto ${question.length >= 2000 ? 'text-red-500 font-semibold' : 'text-gray-500'}`}>
                    {question.length}/2000
                  </span>
                </label>
                <textarea
                  ref={questionTextareaRef}
                  placeholder="Enter your question here..."
                  value={question}
                  onChange={(e) => {
                    if (e.target.value.length <= 2000) {
                      setQuestion(e.target.value);
                    }
                  }}
                  className="w-full min-h-[120px] resize-none border-2 border-red-300 focus:border-red-500 transition-colors p-3 rounded"
                  required
                  maxLength={2000}
                />
                <p className="text-sm text-gray-600">Be clear and specific in your question wording</p>
              </div>

              <Separator.Root className="bg-gray-200 h-px w-full" />

              {/* Source Material Context */}
              <div className="space-y-2">
                <label className="text-base font-bold flex items-center gap-2 text-black">
                  Source Material Context
                  <span className="bg-gray-200 text-black text-xs px-2 py-1 rounded">
                    Optional
                  </span>
                  <span className={`text-xs ml-auto ${sourceContext.length >= 500 ? 'text-red-500 font-semibold' : 'text-gray-500'}`}>
                    {sourceContext.length}/500
                  </span>
                </label>
                <textarea
                  placeholder="If this question is based on specific source material, provide relevant context here (e.g., 'Shakespeare's Macbeth - focusing on themes of ambition and guilt' or 'The Great Gatsby - analysis of the American Dream')"
                  value={sourceContext}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setSourceContext(e.target.value);
                    }
                  }}
                  className="w-full min-h-[100px] resize-none border-2 border-gray-300 focus:border-gray-500 transition-colors p-3 rounded"
                  maxLength={500}
                />
                
                {/* Tip moved here */}
                <div className="border border-gray-300 bg-gray-50 p-3 rounded-lg mt-3">
                  <div className="flex items-start gap-2">
                    <span className="text-gray-600 text-lg">💡</span>
                    <div className="text-gray-700 text-sm">
                      <strong>Tip:</strong> Source material context helps generate better marking rubrics and provides valuable context during the marking process to know what specific elements to look for in student responses.
                    </div>
                  </div>
                </div>
              </div>

              <Separator.Root className="bg-gray-200 h-px w-full" />

              {/* Marks Section */}
              <div className="space-y-4">
                <label className="text-base font-bold text-black">Marking Information</label>

                <div className="space-y-4">
                  {/* Total Marks and Range Slider on same line */}
                  <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
                    {/* Total Marks Input and Range Controls */}
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <label className="text-sm font-semibold flex items-center gap-2 text-black">
                          Total Marks
                          <span className="bg-red-600 text-white text-xs px-2 py-1 rounded">
                            Required
                          </span>
                        </label>
                        <input
                          type="number"
                          placeholder="Enter total marks"
                          value={marks}
                          onChange={handleMarksChange}
                          className="w-full border-2 border-red-300 focus:border-red-500 transition-colors p-3 rounded"
                          required
                        />
                      </div>

                      {/* Number inputs for fine-tuning - moved under Total Marks */}
                      {parseInt(marks) > 0 && (
                        <div className="space-y-3">
                          <div className="space-y-1">
                            <label className="text-xs font-medium text-gray-700">Low Range End</label>
                            <input
                              type="number"
                              min="0"
                              max={showMediumRange ? (lowMarkRange ? parseInt(lowMarkRange.split('-')[1]) : 0) : parseInt(marks) - 1}
                              value={lowMarkRange ? parseInt(lowMarkRange.split('-')[1]) : 0}
                              onChange={(e) => {
                                const newLowEnd = parseInt(e.target.value);
                                if (!isNaN(newLowEnd)) {
                                  const newLowRange = `0-${newLowEnd}`;
                                  const currentMediumEnd = mediumMarkRange ? parseInt(mediumMarkRange.split('-')[1]) : 0;
                                  const newHighRange = showMediumRange ? `${currentMediumEnd + 1}-${marks}` : `${newLowEnd + 1}-${marks}`;
                                  handleRangeChange(newLowRange, mediumMarkRange, newHighRange);
                                }
                              }}
                              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                            />
                            <div className="text-xs text-gray-500">Range: 0-{lowMarkRange ? parseInt(lowMarkRange.split('-')[1]) : 0}</div>
                          </div>

                          {showMediumRange && (
                            <div className="space-y-1">
                              <label className="text-xs font-medium text-gray-700">Medium Range End</label>
                              <input
                                type="number"
                                min={(lowMarkRange ? parseInt(lowMarkRange.split('-')[1]) : 0) + 1}
                                max={parseInt(marks) - 1}
                                value={mediumMarkRange ? parseInt(mediumMarkRange.split('-')[1]) : 0}
                                onChange={(e) => {
                                  const newMediumEnd = parseInt(e.target.value);
                                  if (!isNaN(newMediumEnd)) {
                                    const lowEnd = lowMarkRange ? parseInt(lowMarkRange.split('-')[1]) : 0;
                                    const newMediumRange = `${lowEnd + 1}-${newMediumEnd}`;
                                    const newHighRange = `${newMediumEnd + 1}-${marks}`;
                                    handleRangeChange(lowMarkRange, newMediumRange, newHighRange);
                                  }
                                }}
                                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                              />
                              <div className="text-xs text-gray-500">Range: {lowMarkRange ? parseInt(lowMarkRange.split('-')[1]) + 1 : 1}-{mediumMarkRange ? parseInt(mediumMarkRange.split('-')[1]) : 0}</div>
                            </div>
                          )}
                          
                          <div className="text-xs text-gray-600">
                            High range: {showMediumRange ? (mediumMarkRange ? parseInt(mediumMarkRange.split('-')[1]) + 1 : 1) : (lowMarkRange ? parseInt(lowMarkRange.split('-')[1]) + 1 : 1)}-{marks} (automatically calculated)
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Range Slider - takes up remaining 3 columns */}
                    <div className="lg:col-span-3 space-y-4">
                      <div className="flex items-center justify-between">
                        <div></div>
                        {!showMediumRange && (
                          <Button
                            type="button"
                            variant="soft"
                            size="2"
                            onClick={addMediumRange}
                            disabled={parseInt(marks) < 3}
                            className={`border-gray-400 text-black hover:bg-gray-50 bg-transparent ${
                              parseInt(marks) < 3 ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                            title={parseInt(marks) < 3 ? 'Medium range requires at least 3 total marks' : ''}
                          >
                            ➕ Add Medium Range
                          </Button>
                        )}
                        {showMediumRange && (
                          <Button
                            type="button"
                            variant="soft"
                            size="2"
                            onClick={removeMediumRange}
                            className="border-red-400 text-red-500 hover:bg-red-50 bg-transparent"
                          >
                            ✕ Remove Medium Range
                          </Button>
                        )}
                      </div>

                      {parseInt(marks) > 0 ? (
                        <RangeSlider
                          totalMarks={parseInt(marks)}
                          showMediumRange={showMediumRange}
                          onRangeChange={handleRangeChange}
                          lowRange={lowMarkRange}
                          mediumRange={mediumMarkRange}
                          highRange={highMarkRange}
                        />
                      ) : (
                        <div className="text-gray-500 text-center py-8">
                          Please enter total marks first to set up mark ranges
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <Separator.Root className="bg-gray-200 h-px w-full" />

              {/* Rubric Generation */}
              <div className="space-y-4">
                <label className="text-base font-bold text-black">Marking Rubric</label>

                {!showRubricEditor ? (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-700">
                      Choose how you'd like to create the marking rubric for this question:
                    </p>

                    {(!question.trim() || !marks || !lowMarkRange || !highMarkRange) && (
                      <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                        <p className="text-sm text-blue-800">
                          💡 Please enter a question and total marks first to enable rubric creation options.
                        </p>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div
                        className={`transition-all border-2 rounded-lg p-4 text-center space-y-3 ${
                          !question.trim() || !marks || !lowMarkRange || !highMarkRange
                            ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed opacity-60'
                            : 'cursor-pointer hover:shadow-lg border-gray-300 hover:border-gray-400 bg-white hover:bg-gray-50 text-black'
                        }`}
                        onClick={() => handleRubricChoice("manual")}
                      >
                        <div className="text-2xl">📚</div>
                        <h3 className="font-bold">Write Rubric Manually</h3>
                        <p className="text-sm">
                          Create detailed marking criteria yourself
                        </p>
                      </div>

                      <div
                        className={`transition-all border-2 rounded-lg p-4 text-center space-y-3 ${
                          !question.trim() || !marks || !lowMarkRange || !highMarkRange
                            ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed opacity-60'
                            : 'cursor-pointer hover:shadow-lg border-purple-300 hover:border-purple-400 bg-white hover:bg-purple-50 text-black'
                        }`}
                        onClick={() => handleRubricChoice("ai")}
                      >
                        <div className="text-2xl">🧠✨</div>
                        <h3 className="font-bold">Generate with AI</h3>
                        <p className="text-sm">
                          Let AI create comprehensive marking criteria automatically
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <RubricEditor
                    method={rubricMode!}
                    lowRange={lowMarkRange}
                    mediumRange={showMediumRange ? mediumMarkRange : null}
                    highRange={highMarkRange}
                    rubric={rubric}
                    setRubric={setRubric}
                    loading={loading}
                    handleGenerateRubric={handleGenerateRubric}
                    onBack={() => setShowRubricEditor(false)}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-end">
            <Button
              variant="soft"
              size="3"
              className="bg-white border-2 border-black text-black hover:bg-gray-50"
              onClick={() => navigate('/view-questions')}
            >
              Cancel
            </Button>
            <Button
              size="3"
              className="bg-black hover:bg-gray-800 text-white font-semibold"
              disabled={!question.trim() || !marks || !rubricGenerated}
              onClick={handleSaveQuestion}
            >
              💾 Save Question
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

// RubricEditor Component
interface RubricEditorProps {
  method: 'manual' | 'ai';
  lowRange: string;
  mediumRange: string | null;
  highRange: string;
  rubric: RubricState;
  setRubric: React.Dispatch<React.SetStateAction<RubricState>>;
  loading: boolean;
  handleGenerateRubric: () => void;
  onBack: () => void;
}

const RubricEditor: React.FC<RubricEditorProps> = ({
  method,
  lowRange,
  mediumRange,
  highRange,
  rubric,
  setRubric,
  loading,
  handleGenerateRubric,
  onBack,
}) => {
  const lowRubricRef = useRef<HTMLTextAreaElement>(null);
  const mediumRubricRef = useRef<HTMLTextAreaElement>(null);
  const highRubricRef = useRef<HTMLTextAreaElement>(null);

  const adjustTextareaHeight = (ref: React.RefObject<HTMLTextAreaElement>) => {
    const textarea = ref.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  };

  useEffect(() => {
    adjustTextareaHeight(lowRubricRef);
  }, [rubric.low]);

  useEffect(() => {
    adjustTextareaHeight(mediumRubricRef);
  }, [rubric.medium]);

  useEffect(() => {
    adjustTextareaHeight(highRubricRef);
  }, [rubric.high]);

  const isAI = method === 'ai';
  const cardBorderColor = isAI ? 'border-purple-300' : 'border-gray-300';
  const buttonColor = isAI ? 'bg-purple-600 hover:bg-purple-700' : 'bg-black hover:bg-gray-800';

  return (
    <div className={`border-2 border-dashed ${cardBorderColor} bg-white rounded-lg`}>
      <div className="p-4 pb-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-black flex items-center gap-2">
            {isAI ? (
              <>
                🧠 AI-Generated Rubric
              </>
            ) : (
              <>
                📚 Manual Rubric Creation
              </>
            )}
          </h3>
          <button 
            onClick={onBack}
            className="text-black hover:bg-gray-50 px-2 py-1 rounded text-sm"
          >
            ← Back
          </button>
        </div>
      </div>
      <div className="p-4 space-y-4">
        {isAI && !rubric.low && !rubric.high && (
          <div className="text-center py-8 space-y-4">
            <div className="text-4xl">🧠✨</div>
            <div>
              <h3 className="font-bold mb-2 text-black">Ready to Generate Rubric</h3>
              <p className="text-sm text-gray-700 mb-4">
                AI will create detailed marking criteria based on your question and mark ranges.
              </p>
              <Button 
                onClick={handleGenerateRubric} 
                disabled={loading} 
                className={buttonColor}
                size="3"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    ✨ Generate Rubric with AI
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {(!isAI || rubric.low || rubric.high) && (
          <div className={`grid grid-cols-1 ${mediumRange ? 'lg:grid-cols-3' : 'md:grid-cols-2'} gap-4`}>
            <div className="space-y-2">
              <label className="font-semibold text-black">Low Mark Range ({lowRange || '0-1'}):</label>
              <textarea
                ref={lowRubricRef}
                placeholder="Write the rubric for low mark range..."
                value={rubric.low}
                onChange={(e) => {
                  setRubric(prev => ({ ...prev, low: e.target.value }));
                }}
                className="w-full min-h-[120px] resize-none border-2 border-red-400 focus:border-red-500 p-3 rounded overflow-hidden"
              />
            </div>

            {mediumRange && (
              <div className="space-y-2">
                <label className="font-semibold text-black">Medium Mark Range ({mediumRange}):</label>
                <textarea
                  ref={mediumRubricRef}
                  placeholder="Write the rubric for medium mark range..."
                  value={rubric.medium}
                  onChange={(e) => {
                    setRubric(prev => ({ ...prev, medium: e.target.value }));
                  }}
                  className="w-full min-h-[120px] resize-none border-2 border-yellow-400 focus:border-yellow-500 p-3 rounded overflow-hidden"
                />
              </div>
            )}

            <div className="space-y-2">
              <label className="font-semibold text-black">High Mark Range ({highRange || '4-6'}):</label>
              <textarea
                ref={highRubricRef}
                placeholder="Write the rubric for high mark range..."
                value={rubric.high}
                onChange={(e) => {
                  setRubric(prev => ({ ...prev, high: e.target.value }));
                }}
                className="w-full min-h-[120px] resize-none border-2 border-green-400 focus:border-green-500 p-3 rounded overflow-hidden"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InsertNewQuestion; 